import React, { useEffect, useCallback } from 'react';
import { DEFAULT_FILL_OPACITY } from 'utils/constants/Map';
import { createRoot } from 'react-dom/client';
import InfoWindowContent from '../InfoWindow/InfoWindowContent/InfoWindowContent';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

const Polygon = ({
  map,
  maps,
  paths = [],
  draggable,
  color,
  isFilled,
  editable,
  setValues,
  strokeOpacity = 1,
  strokeWeight = 2,
  onPolygonClick,
  zIndex,
  id,
  infoWindow,
  infoWindowData,
  polygonRef,
  polygonsInForm,
  layers,
  switchedLayer,
  polygonData,
}) => {
  const updatePolygonBounds = polygon => {
    const bounds = [];
    const polygonBounds = polygon.getPath();
    for (let i = 0; i < polygonBounds.length; i += 1) {
      const vertice = {
        lat: polygonBounds.getAt(i).lat(),
        lng: polygonBounds.getAt(i).lng(),
      };
      bounds.push(vertice);
    }
    setValues([
      {
        isFilled: true,
        color,
        paths: bounds,
        draggable,
        editable,
        strokeOpacity,
        strokeWeight,
      },
    ]);
  };

  const fitBounds = useCallback(
    ({ zoom }) => {
      const bounds = new maps.LatLngBounds();
      for (const marker of paths) {
        bounds.extend(new maps.LatLng(marker.lat, marker.lng));
      }
      // three conditions
      if (paths.length > 1 && zoom) {
        map.fitBounds(bounds);
      }

      // else if (paths.length === 1) {
      //   const [{ lat, lng }] = paths;
      //   map.setCenter(new maps.LatLng(lat, lng));
      // }
    },
    [paths, map, maps],
  );

  const polygon = new maps.Polygon({
    map: map,
    id: polygonData.id,
    layer: polygonData.layer,
    strokeColor: color,
    strokeOpacity,
    strokeWeight,
    fillColor: color,
    fillOpacity: isFilled ? DEFAULT_FILL_OPACITY : 0,
    draggable,
    editable,
    zIndex,
  });

  const updatePolygonColor = useCallback(() => {
    polygon.setOptions({ fillColor: color });
  }, [color, polygon]);

  useEffect(() => {
    if (switchedLayer.includes(polygonData?.layer)) {
      return undefined; // has to return undefined as it throws destroy is not a function
      // TypeError: destroy is not a function
    }
    fitBounds({ zoom: false });
    updatePolygonColor();
    polygon.setPath(paths);
    const setAtEvent = maps.event.addListener(polygon.getPath(), 'set_at', () =>
      updatePolygonBounds(polygon),
    );
    if (!infoWindow) {
      return undefined;
    }

    const content = document.createElement('div');
    const root = createRoot(content);
    root.render(
      React.createElement(
        InfoWindowContent,
        { position: infoWindowData },
        null,
      ),
    );

    infoWindow.setContent(content);
    const polygonClick = maps.event.addListener(polygon, 'click', e => {
      if (polygonsInForm) {
        maps.event.trigger(map, 'click', e);
        return;
      }
      // close all other info windows
      polygonRef.current.polygonsInfoWindow.forEach(item => item.close());
      // set the position of the infowindow
      infoWindow.setPosition(e.latLng);
      // open it
      infoWindow.open(map);
    });

    const insertAtEvent = maps.event.addListener(
      polygon.getPath(),
      'insert_at',
      () => updatePolygonBounds(polygon),
    );

    if (polygonRef.current?.[polygonData.layer]) {
      polygonRef.current[polygonData.layer].push(polygon);
    } else {
      Object.assign(polygonRef.current, {
        [polygonData.layer]: [],
      });
      polygonRef.current[polygonData.layer].push(polygon);
    }

    // close the infowindow
    maps.event.addListener(infoWindow, 'domready', () => {
      const someButton = document.getElementById(
        `btn-click-${infoWindowData.pageName}`,
      );
      if (someButton) {
        someButton.addEventListener('click', e => {
          //close the infowindow
          infoWindow.setMap(null);
          infoWindow.close();
          if (typeof onPolygonClick === 'function') {
            onPolygonClick({
              event: e,
              id,
              pageName: infoWindowData.pageName,
              onClickfitBounds: fitBounds,
            });
          }
          maps.event.trigger(map, 'click', e);
        });
      }
    });
    // keep track of info windows
    if (polygonRef.current?.polygons) {
      polygonRef.current.polygonsInfoWindow.push(infoWindow);
    } else {
      Object.assign(polygonRef.current, {
        polygonsInfoWindow: [],
      });
      polygonRef.current.polygonsInfoWindow.push(infoWindow);
    }

    return () => {
      maps.event.removeListener(setAtEvent);
      maps.event.removeListener(insertAtEvent);
      maps.event.removeListener(polygonClick);
      infoWindow.setMap(null);
      polygon.setMap(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitBounds, updatePolygonColor]);

  // remove the polygon layers
  useEffect(() => {
    if (
      !isEmpty(polygonRef.current) &&
      Object.keys(layers).some(k => !layers[k])
    ) {
      switchedLayer.forEach(f => {
        if (!polygonRef.current[f]) {
          polygonRef.current[f] = [];
        }

        const copy = [...polygonRef.current[f]];
        copy.forEach((poly, i) => {
          if (f === poly.layer) {
            poly.setVisible(false);
            polygonRef.current[f].splice(i, 1);
          }
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers, map, paths]);
  return null;
};
Polygon.propTypes = {
  maps: PropTypes.object,
  paths: PropTypes.array,
  draggable: PropTypes.bool,
  color: PropTypes.string,
  isFilled: PropTypes.bool,
  editable: PropTypes.bool,
  setValues: PropTypes.func,
  strokeOpacity: PropTypes.number,
  strokeWeight: PropTypes.number,
  onPolygonClick: PropTypes.func,
  zIndex: PropTypes.number,
  id: PropTypes.string,
  infoWindow: PropTypes.object,
  infoWindowData: PropTypes.object,
  polygonRef: PropTypes.object,
  map: PropTypes.object,
  polygonsInForm: PropTypes.bool,
  layers: PropTypes.object,
  switchedLayer: PropTypes.array,
  polygonData: PropTypes.object,
};
export default Polygon;
